/**
 * EventManager.ts
 *
 * 이벤트 CRUD(Create, Read, Update, Delete) 작업을 담당하는 모듈
 *
 * 주요 기능:
 * - 이벤트 생성, 수정, 삭제
 * - 이벤트 시간 변경 및 드래그 앤 드롭 처리
 * - 반복 이벤트 처리 (이번만, 이후 모두, 전체 삭제)
 * - 미팅 응답 처리 (수락, 거절, 임시)
 * - 이벤트 검색 및 이동
 * - ICS 파일 생성 및 파싱
 *
 * 이 모듈은 사용자의 이벤트 관련 모든 작업을 처리하며,
 * CalDAV 서버와의 이벤트 데이터 교환을 담당합니다.
 */

import moment from 'moment'
import { RRule } from 'rrule'
import { parseICS } from 'node-ical'
import { VEvent } from 'node-ical'
import { convertToRRuleOptions } from '../utils/rruleCompat'

import { generateICS } from './ICSGenerator'
import { doMeetingResponse } from './EventEditProcessor'
import { EventData } from '../components/EventEditor/EventData'
import * as dateUtils from '../utils/DateUtils'

import { classInfo, userInfo, instanceEmitter, Calendar } from './CalendarState'
import { getClient } from './CalendarClient'
import {
  _removeInstances,
  _createInstances,
  findInstanceByUid,
  EventsDict,
} from './EventInstanceManager'
import { refineEvent } from './RefineEvent'
import { reloadEvents, enableSync } from './CalendarSync'
import { showResult, errorBase } from 'utils/ui'
import { generateFilename } from 'utils'
import settings from 'popups/Settings'
import i18n from 'i18n'
import { DAVNamespaceShort } from 'libs/tsdav'
import { isSameDavUrl } from 'libs/dav/davPath'
import type { DAVClient } from 'libs/tsdav/client'

import EventInstance from './EventInstance'

// Type definitions
interface EventDict {
  url: string
  etag: string
  color: string
  event: VEvent
  folderId: string
  dirty?: boolean
}

interface CalendarSettings {
  defaultCalendar?: string
  lastAddedCalendar?: string
  calendarSyncTargets?: string[]
}

// interface CalendarObject {
//   url: string
//   etag: string
//   data?: string
// }

interface DAVResponse {
  status: number
  ok?: boolean
  url?: string
  href?: string
  raw?: ReadableStream
  props?: {
    getetag?: string
    calendarData?: { _cdata: string }
  }
}

interface FreeBusyEntry {
  start: Date
  end: Date
  isAllday: boolean
}

interface FreeBusyResponse {
  [email: string]: FreeBusyEntry[] | null
}

interface AttachmentProgressNotification {
  close: () => void
}

interface MeetingResponseOptions {
  comment?: string
  targetExceptionRecurrenceId?: Date
}

type MeetingResponseType = 'ACCEPTED' | 'DECLINED' | 'TENTATIVE'

// Completion callback types
type SuccessCallback = (success: boolean) => void
type SuccessCodeCallback = (success: boolean, code?: number) => void
type EventCallback = (event: EventInstance | null) => void
type InstancesCallback = (instances: EventInstance[]) => void
type FreeBusyCallback = (freeBusy: FreeBusyResponse) => void

// Event delete types
export const EventDeleteType = {
  ALL: 0,
  THIS_ONLY: 1,
  AFTER_ALL: 2,
} as const

type EventDeleteTypeValue = (typeof EventDeleteType)[keyof typeof EventDeleteType]

// Helper functions
const getCalendars = (): Calendar[] => {
  return classInfo.calendars
}

const enableCalendarSyncByEventServerId = (eventServerId: string): void => {
  const calendar = getCalendars().find((c) => {
    return eventServerId.startsWith(c.url)
  })
  if (calendar && calendar.isSync === false) {
    enableSync(calendar.url, true)
  }
}

/**
 * 이벤트 삭제 함수
 * @param instance 삭제할 이벤트 인스턴스
 * @param deleteType 삭제 타입 (전체, 이번만, 이후 모두)
 * @param completeCallback 완료 콜백
 */
export const deleteEvent = (
  instance: EventInstance,
  deleteType: EventDeleteTypeValue = EventDeleteType.ALL,
  completeCallback: SuccessCallback = (_success: boolean) => {},
): void => {
  const client = getClient() as any as DAVClient

  if (deleteType !== EventDeleteType.ALL) {
    if (deleteType === EventDeleteType.THIS_ONLY) {
      if (instance.exception) {
        let exceptions = instance.event!.recurrences || {}
        const dateStr = moment(instance.exception.recurrenceid).format('YYYY-MM-DD')
        delete exceptions[dateStr]
        instance.event!.recurrences = exceptions
        if (!instance.event!.exdate) {
          instance.event!.exdate = {}
        }
        instance.event!.exdate[dateStr] = instance.exception.recurrenceid as Date
      } else {
        if (!instance.event!.exdate) {
          instance.event!.exdate = {}
        }
        const dateStr = moment(instance.startDate).format('YYYY-MM-DD')
        instance.event!.exdate[dateStr] = instance.startDate
      }
    } else {
      const rrule = instance.event!.rrule!
      const rruleOptions = convertToRRuleOptions(
        rrule.options as unknown as Record<string, unknown>,
      )
      let startDate: Date

      if (instance.exception) {
        startDate = instance.exception.recurrenceid as Date
      } else {
        startDate = instance.startDate
      }

      const untilDate = rrule.before(startDate)
      if (untilDate == null) {
        console.error(
          'deleteEvent(AFTER_ALL): rrule.before() returned null — no earlier occurrence exists',
          { uid: instance.event!.uid, startDate },
        )
        errorBase({
          title: i18n.t('calendar.result.deleteTitle'),
          content: i18n.t('calendar.error.deleteAfterAllNoOccurrence'),
        })
        completeCallback(false)
        return
      }
      if (rruleOptions.count) {
        rruleOptions.count = null
      }
      rruleOptions.until = untilDate as Date

      const newRrule = new RRule(rruleOptions)
      ;(instance.event! as unknown as Record<string, unknown>).rrule = newRrule
    }

    const ics = generateICS(instance.event!)
    client
      .updateCalendarObject({
        calendarObject: { url: instance.serverId, etag: instance.etag, data: ics },
      })
      .then((response: DAVResponse) => {
        console.log('update event success %o', response)
        _removeInstances({ [instance.serverId]: instance as any } as unknown as EventsDict)
        let e = { ...instance.event!, dirty: true }
        let eventDict: any = {
          url: instance.serverId,
          etag: instance.etag,
          color: instance.color,
          event: e,
          folderId: (instance as any).folderId,
          dirty: true,
        }
        classInfo.instances = _createInstances(
          {
            [instance.serverId]: eventDict,
          } as EventsDict,
          true,
          classInfo.instances,
        )
        instanceEmitter.emit('instance')
        enableCalendarSyncByEventServerId(instance.serverId)
        reloadEvents()
        completeCallback(true)
      })
      .catch((error: Error) => {
        console.log('update event failed : %o', error)
        completeCallback(false)
      })
  } else {
    client
      .deleteCalendarObject({
        calendarObject: { url: instance.serverId, etag: instance.etag },
      })
      .then(() => {
        console.log('delete event success')

        const cal = getCalendars().find((c) => {
          return instance.serverId.startsWith(c.url)
        })
        if (cal && (cal as any).events) {
          delete (cal as any).events[instance.serverId]
        }
        _removeInstances({ [instance.serverId]: instance } as unknown as EventsDict)
        completeCallback(true)
      })
      .catch((error: Error) => {
        console.log('delete event failed : %o', error)
        completeCallback(false)
      })
  }
}

/**
 * UID로 이벤트 삭제
 * @param uid 이벤트 UID
 * @param recurrenceId 반복 이벤트의 recurrence ID
 * @param completeCallback 완료 콜백
 */
export const deleteEventByUid = (
  uid: string,
  recurrenceId: Date | null,
  completeCallback: SuccessCallback = (_success: boolean) => {},
): void => {
  findInstanceByUid(uid, recurrenceId, (instance: EventInstance | null) => {
    if (instance) {
      deleteEvent(instance, EventDeleteType.ALL, completeCallback)
    } else {
      completeCallback(false)
    }
  })
}

/**
 * 새 이벤트 생성
 * @param vevent VEvent 객체
 * @param calendar 대상 캘린더
 * @param attachments 첨부파일 목록
 * @param completeCallback 완료 콜백
 */
export const createEvent = (
  vevent: VEvent,
  calendar: Calendar,
  attachments: Array<{ file: File }> = [],
  completeCallback: SuccessCodeCallback = (_success: boolean, _code?: number) => {},
): void => {
  const client = getClient() as any as DAVClient

  ;(vevent as any)['NI-AUTHOR'] = {
    val: userInfo.email,
    params: {
      CN: userInfo.name,
    },
  }

  const ics = generateICS(vevent)
  client
    .createCalendarObject({
      calendar: calendar,
      iCalString: ics,
      filename: `${vevent.uid}.ics`,
    })
    .then((response: DAVResponse) => {
      if (Math.trunc(response.status / 100) === 2) {
        console.log('create event success %o', response)
        if (attachments.length > 0) {
          let count = attachments.length
          const notiObj = _showAttachmentProgress(count)
          attachments.forEach((attachment) => {
            addAttachment(response.url!, attachment.file, (_success: boolean) => {
              count--
              if (count === 0) {
                completeCallback(true, 0)
                enableSync(calendar.url, true)
                reloadEvents()
                notiObj.close()
              }
            })
          })
        } else {
          completeCallback(true, 0)
          enableSync(calendar.url, true)
          reloadEvents()
        }

        const localSettings = settings.getValue('localSettings')
        settings.setValue('localSettings', {
          ...localSettings,
          calendar: {
            ...localSettings.calendar,
            lastAddedCalendar: calendar.url,
          },
        })
      } else {
        console.error('create event failed code : %o', response)
        completeCallback(false, response.status)
      }
    })
    .catch((error: any) => {
      console.error('create event failed catch : %o', error)
      completeCallback(false, error.status)
    })
}

/**
 * 이벤트 업데이트
 * @param serverId 이벤트 서버 ID
 * @param etag ETag
 * @param vevent VEvent 객체
 * @param moveToCalendar 이동할 캘린더 (옵션)
 * @param willAddAttachments 추가할 첨부파일
 * @param willDeleteAttachments 삭제할 첨부파일
 * @param completeCallback 완료 콜백
 */
export const updateEvent = (
  serverId: string,
  etag: string,
  vevent: VEvent,
  moveToCalendar: Calendar | null = null,
  willAddAttachments: Array<{ file: File }> = [],
  willDeleteAttachments: any[] = [],
  completeCallback: SuccessCallback = (_success: boolean) => {},
): void => {
  const client = getClient() as any as DAVClient

  const completeProcess = (): void => {
    enableCalendarSyncByEventServerId(serverId)
    reloadEvents()
    completeCallback(true)
  }

  const ics = generateICS(vevent)
  client
    .updateCalendarObject({ calendarObject: { url: serverId, etag: etag, data: ics } })
    .then((response: DAVResponse) => {
      console.log('update event success %o / do move %d', response, !!moveToCalendar)
      if (
        !moveToCalendar &&
        willAddAttachments.length === 0 &&
        willDeleteAttachments.length === 0
      ) {
        completeProcess()
      } else if (willAddAttachments.length > 0 || willDeleteAttachments.length > 0) {
        const afterMove = (): void => {
          if (moveToCalendar) {
            moveEvent(moveToCalendar, serverId, (_success: boolean) => {
              completeProcess()
            })
          } else {
            completeProcess()
          }
        }

        batchUpdateAttachments(
          serverId,
          willAddAttachments,
          willDeleteAttachments,
          false,
          (_success: boolean) => {
            afterMove()
          },
        )
      } else if (moveToCalendar) {
        moveEvent(moveToCalendar, serverId, (success: boolean) => {
          completeCallback(success)
        })
      }
    })
    .catch((error: Error) => {
      console.log('update event failed : %o', error)
      completeCallback(false)
    })
}

/**
 * 이벤트 시간 변경
 * @param instance 이벤트 인스턴스
 * @param newStartTime 새 시작 시간
 * @param newEndTime 새 종료 시간
 * @param toAllday 종일 이벤트로 변경 여부
 * @param completeCallback 완료 콜백
 */
export const updateEventChangeTime = (
  instance: EventInstance,
  newStartTime: Date & { tz?: string },
  newEndTime: Date & { tz?: string },
  toAllday: boolean,
  completeCallback: SuccessCallback = (_success: boolean) => {},
): void => {
  if (!toAllday) {
    if (!newStartTime.tz) {
      newStartTime.tz = instance.startTz || moment.tz.guess()
    }
    if (!newEndTime.tz) {
      newEndTime.tz = instance.endTz || moment.tz.guess()
    }
  } else {
    newEndTime = dateUtils.dateAddingDate(newEndTime, -1) as Date & { tz?: string }
  }

  if (
    instance.startDate.valueOf() === newStartTime.valueOf() &&
    instance.endDate.valueOf() === newEndTime.valueOf()
  ) {
    completeCallback(true)
    return
  }

  const isRecurring = instance.isRecurring

  const eventData = new EventData(instance, null, isRecurring ? 'this' : null, null)
  eventData.attendees.forEach((attendee) => {
    attendee.response = 'NEEDS-ACTION'
  })
  eventData.optionalAttendees.forEach((attendee) => {
    attendee.response = 'NEEDS-ACTION'
  })
  eventData.startDate = newStartTime
  eventData.startTz = newStartTime.tz!
  if (toAllday) {
    newEndTime.setDate(newEndTime.getDate() - 1)
  }
  eventData.endDate = newEndTime
  eventData.endTz = newEndTime.tz!
  if (eventData.isAllDay !== toAllday) {
    eventData.isBusy = toAllday ? false : true
  }
  eventData.isAllDay = toAllday
  eventData.sequence = eventData.sequence ? (parseInt(eventData.sequence) + 1).toString() : '0'

  let vevent = eventData.toVEvent()
  const author = (vevent as any)['NI-AUTHOR']
  if (!author) {
    ;(vevent as any)['NI-AUTHOR'] = {
      val: userInfo.email,
      params: {
        CN: userInfo.name,
      },
    }
  }

  if (isRecurring) {
    const targetEvent = instance.event! as any
    // 예외 딕셔너리 조회 키는 저장 시점의 toAllday 가 아니라 마스터의 종일 여부를 따른다.
    // 종일 마스터의 vevent.start.tz='Etc/UTC' 는 ICS 출력용 라벨일 뿐 recurrenceid 는 로컬
    // 자정이므로, tz 변환하면 UTC+ 지역에서 키가 하루 앞으로 밀린다.
    const isMasterAllDay = instance.event!.datetype === 'date'
    const rKey = isMasterAllDay
      ? moment((vevent as any).recurrenceid).format('YYYY-MM-DD')
      : moment((vevent as any).recurrenceid)
          .tz((vevent.start as any).tz || moment.tz.guess())
          .format('YYYY-MM-DD')
    if (targetEvent.recurrences) {
      targetEvent.recurrences[rKey] = vevent as any
    } else {
      targetEvent.recurrences = { [rKey]: vevent as any }
    }
    vevent = targetEvent
  }

  updateEvent(instance.serverId, instance.etag, vevent as any, null, [], [], completeCallback)
}

/**
 * 첨부파일 진행 상태 표시
 * @param count 첨부파일 개수
 * @returns 알림 객체
 */
const _showAttachmentProgress = (count: number): AttachmentProgressNotification => {
  const notiObj = showResult({
    message:
      count === 1
        ? i18n.t('calendar.result.uploadingAttachment')
        : i18n.t('calendar.result.uploadingAttachments', { count }),
    loading: true,
    duration: 0,
    closeAfterAction: false,
    closeOnClick: false,
  })

  return notiObj as AttachmentProgressNotification
}

/**
 * 첨부파일 일괄 업데이트
 * @param eventServerId 이벤트 서버 ID
 * @param willAdds 추가할 첨부파일
 * @param willDelete 삭제할 첨부파일
 * @param updateAfterProcess 처리 후 업데이트 여부
 * @param completeCallback 완료 콜백
 */
export const batchUpdateAttachments = (
  eventServerId: string,
  willAdds: Array<{ file: File }> = [],
  willDelete: any[] = [],
  updateAfterProcess: boolean = false,
  completeCallback: SuccessCallback = (_success: boolean) => {},
): void => {
  const addCount = willAdds.length
  let count = addCount + willDelete.length
  const notiObj = addCount > 0 ? _showAttachmentProgress(addCount) : null

  const completeProcess = (): void => {
    if (updateAfterProcess) {
      enableCalendarSyncByEventServerId(eventServerId)
      reloadEvents()
    }
    completeCallback(true)
    if (notiObj) {
      notiObj.close()
    }
  }

  willAdds.forEach((attachment) => {
    addAttachment(eventServerId, attachment.file, (_success: boolean) => {
      count--
      if (count === 0) {
        completeProcess()
      }
    })
  })
  willDelete.forEach((attachment) => {
    deleteAttachment(eventServerId, attachment.params['MANAGED-ID'], (_success: boolean) => {
      count--
      if (count === 0) {
        completeProcess()
      }
    })
  })
}

/**
 * 첨부파일 추가
 * @param eventServerId 이벤트 서버 ID
 * @param file 파일 객체
 * @param completeCallback 완료 콜백
 */
export const addAttachment = (
  eventServerId: string,
  file: File,
  completeCallback: SuccessCallback = (_success: boolean) => {},
): void => {
  const client = getClient() as any as DAVClient
  client
    .davRequest({
      url: eventServerId + '?action=attachment-add',
      init: {
        method: 'POST',
        headers: {
          'Content-Type': file.type,
          'Content-Disposition': `attachment; filename="${encodeURIComponent(file.name)}"`,
          Prefer: 'return=representation',
        },
        body: file,
      },
      convertIncoming: false,
    })
    .then((response: DAVResponse[]) => {
      console.log('add attachment success %o', response)
      completeCallback(true)
    })
    .catch((error: Error) => {
      console.log('add attachment failed : %o', error)
      completeCallback(false)
    })
}

/**
 * 첨부파일 삭제
 * @param eventServerId 이벤트 서버 ID
 * @param attachmentId 첨부파일 ID
 * @param completeCallback 완료 콜백
 */
export const deleteAttachment = (
  eventServerId: string,
  attachmentId: string,
  completeCallback: SuccessCallback = (_success: boolean) => {},
): void => {
  const client = getClient() as any as DAVClient
  client
    .davRequest({
      url: eventServerId + '?action=attachment-remove&managed-id=' + attachmentId,
      init: {
        method: 'POST',
        headers: {
          Prefer: 'return=representation',
        },
        body: null,
      },
    })
    .then((response: DAVResponse[]) => {
      console.log('delete attachment success %o', response)
      completeCallback(true)
    })
    .catch((error: Error) => {
      console.log('delete attachment failed : %o', error)
      completeCallback(false)
    })
}

/**
 * 첨부파일 다운로드
 * @param attachmentPath 첨부파일 경로
 * @param filename 파일명
 * @param completeCallback 완료 콜백
 */
export const fetchAttachment = (
  attachmentPath: string,
  filename: string,
  completeCallback: SuccessCallback = (_success: boolean) => {},
): void => {
  const url = attachmentPath

  const client = getClient() as any as DAVClient
  client
    .davRequest({
      url,
      init: {
        method: 'GET',
        attributes: {
          fetchData: 'download',
        },
        body: null,
      },
      convertIncoming: false,
      parseOutgoing: false,
    })
    .then((response: DAVResponse[]) => {
      console.log('fetch attachment success %o', response)

      streamToBlob(response[0].raw!)
        .then((blob: Blob) => {
          console.log('fetch attachment file %o', blob)
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = generateFilename(filename)
          a.click()
          URL.revokeObjectURL(url)
          completeCallback(true)
        })
        .catch((error: Error) => {
          console.log('fetch attachment failed : %o', error)
          completeCallback(false)
        })
    })
    .catch((error: Error) => {
      console.log('fetch attachment failed : %o', error)
      completeCallback(false)
    })
}

/**
 * ReadableStream을 Blob으로 변환
 * @param stream ReadableStream
 * @returns Blob Promise
 */
const streamToBlob = async (stream: ReadableStream): Promise<Blob> => {
  const reader = stream.getReader()
  const chunks: any[] = []

  const read = async (): Promise<Blob> => {
    const { done, value } = await reader.read()
    if (done) {
      return new Blob(chunks)
    }
    chunks.push(value)
    return read()
  }
  return read()
}

/**
 * 미팅 응답 처리
 * @param instance 이벤트 인스턴스
 * @param comment 코멘트
 * @param completeCallback 완료 콜백
 */
export const meetingResponse = (
  instance: EventInstance,
  comment: string,
  completeCallback: SuccessCallback = (_success: boolean) => {},
): void => {
  const client = getClient() as any as DAVClient

  const options: MeetingResponseOptions = {
    comment,
    targetExceptionRecurrenceId: instance.exception?.recurrenceid as Date,
  }

  const ics = generateICS(instance.event!, options)
  client
    .updateCalendarObject({
      calendarObject: { url: instance.serverId, etag: instance.etag, data: ics },
    })
    .then((response: DAVResponse) => {
      console.log('update event success %o', response)
      completeCallback(true)
      enableCalendarSyncByEventServerId(instance.serverId)
      reloadEvents()
    })
    .catch((error: Error) => {
      console.log('update event failed : %o', error)
      completeCallback(false)
    })
}

/**
 * UID로 미팅 응답 처리
 * @param uid 이벤트 UID
 * @param recurrenceId 반복 이벤트 ID
 * @param responseType 응답 타입
 * @param comment 코멘트
 * @param completeCallback 완료 콜백
 */
export const meetingResponseByUid = (
  uid: string,
  recurrenceId: Date | null,
  responseType: MeetingResponseType,
  comment: string,
  completeCallback: SuccessCallback = (_success: boolean) => {},
): void => {
  searchEventByUid(uid, recurrenceId, (instance: EventInstance | null) => {
    if (instance) {
      doMeetingResponse(
        instance,
        responseType,
        recurrenceId ? 'this' : 'all',
        comment,
        completeCallback,
      )
    } else {
      completeCallback(false)
      //TODO: New Event.....
    }
  })
}

/**
 * UID로 이벤트 검색
 * @param uid 이벤트 UID
 * @param recurrenceId 반복 이벤트 ID
 * @param completeCallback 완료 콜백
 */
export const searchEventByUid = (
  uid: string,
  recurrenceId: Date | null,
  completeCallback: EventCallback = (_event: EventInstance | null) => {},
): void => {
  const client = getClient() as any as DAVClient

  const xml =
    '<?xml version="1.0" encoding="utf-8" ?>' +
    '<C:calendar-query xmlns:C="urn:ietf:params:xml:ns:caldav">' +
    '<D:prop xmlns:D="DAV:">' +
    '<D:getetag/>' +
    '<C:calendar-data/>' +
    '</D:prop>' +
    '<C:filter>' +
    '<C:comp-filter name="VCALENDAR">' +
    '<C:comp-filter name="VEVENT">' +
    '<C:prop-filter name="UID">' +
    '<C:text-match collation="i;unicode-casemap">' +
    uid +
    '</C:text-match>' +
    '</C:prop-filter>' +
    '</C:comp-filter>' +
    '</C:comp-filter>' +
    '</C:filter>' +
    '</C:calendar-query>'

  client
    .davRequest({
      url: client.account.homeUrl,
      init: {
        method: 'REPORT',
        headers: {
          Depth: 'infinity',
        },
        body: xml,
      },
      convertIncoming: false,
    })
    .then((response: DAVResponse[]) => {
      console.log('enter search event by uid success %o', response)
      const r = response.find((r) => {
        if (r.ok && r.href) {
          const urlComponents = r.href.split('/')
          urlComponents.pop()
          urlComponents.push('')
          const calendarUrl = urlComponents.join('/')
          const folder = classInfo.calendars.find((c: Calendar) => c.url.endsWith(calendarUrl))
          if (folder) {
            return true
          }
        }
        return false
      })
      if (r) {
        const homeSetUrl = new URL(client.account.homeUrl)
        homeSetUrl.pathname = r.href!
        const url = homeSetUrl.href
        const folder = classInfo.calendars.find((c: Calendar) => url.startsWith(c.url))
        const etag = r.props!.getetag!
        const data = r.props!.calendarData!._cdata
        const vCalendar = parseICS(data)
        let masterEvent: VEvent | null = null
        Object.entries(vCalendar).forEach(([_, v]) => {
          if ((v as any).type === 'VEVENT') {
            masterEvent = v as VEvent
          }
        })
        if (masterEvent) {
          masterEvent = refineEvent(masterEvent)
        } else {
          throw new Error('master event not found')
        }
        const eventDict: any = {
          url: url,
          etag: etag,
          color: (masterEvent as any).color,
          event: masterEvent,
          folderId: folder!.url,
          dirty: true,
        }
        // 반복 인스턴스는 기준일 전월~익월 범위만 전개되므로, 찾으려는 회차를 기준일로 삼는다.
        // 시리즈 최초 DTSTART를 기준으로 하면 먼 미래 회차가 전개되지 않아 검색에 실패한다.
        const instances = _createInstances(
          { [url]: eventDict } as EventsDict,
          false,
          [],
          true,
          recurrenceId ?? masterEvent.start,
        )
        if (recurrenceId) {
          const isSameDate = (date1: Date | null, date2: Date | null): boolean => {
            if (!date1 || !date2) {
              return false
            }
            return (
              date1.getFullYear() === date2.getFullYear() &&
              date1.getMonth() === date2.getMonth() &&
              date1.getDate() === date2.getDate()
            )
          }

          const i = instances.find((i) => {
            return (
              isSameDate(i.startDate, recurrenceId) ||
              isSameDate(i.exception?.recurrenceid as Date, recurrenceId)
            )
          })
          completeCallback(i || null)
        } else {
          completeCallback(instances[0])
        }

        console.log('end search event by uid success %o', instances)
      } else {
        completeCallback(null)
        console.log('search event by uid failed (not found)')
      }
    })
    .catch((error: Error) => {
      console.log('search event by uid failed : %o', error)
    })
}

/**
 * 키워드로 이벤트 검색
 * @param keyword 검색 키워드
 * @param targetFolderUrls 대상 폴더 URL 목록
 * @param startDate 시작 날짜
 * @param endDate 종료 날짜
 * @param completeCallback 완료 콜백
 */
export const searchEventByKeyword = (
  keyword: string | null,
  targetFolderUrls: string[] | null,
  startDate: Date | null,
  endDate: Date | null,
  completeCallback: InstancesCallback = (_instances: EventInstance[]) => {},
): void => {
  if (keyword == null || keyword.length === 0 || startDate == null || endDate == null) {
    completeCallback([])
    return
  }

  const _completeProcess = async (responseUrls: Set<string>): Promise<void> => {
    const targetItemUrls: string[] = []
    const searchFolderUrls = new Set<string>()
    const syncTargets = targetFolderUrls
      ? targetFolderUrls
      : (settings.getValue('calendar') as CalendarSettings).calendarSyncTargets || []
    responseUrls.forEach((url) => {
      const splitedUrl = url.split('/')
      splitedUrl.pop()
      splitedUrl.push('')
      const folderUrl = splitedUrl.join('/')
      const result = syncTargets.find((target) => isSameDavUrl(target, folderUrl))
      if (result) {
        searchFolderUrls.add(folderUrl)
        targetItemUrls.push(url)
      }
    })

    if (targetItemUrls.length === 0) {
      completeCallback([])
      return
    }

    const client = getClient() as any as DAVClient
    client
      .calendarMultiGet({
        url: client.account.homeUrl,
        objectUrls: targetItemUrls,
        props: {
          [`${DAVNamespaceShort.DAV}:getetag`]: {},
          [`${DAVNamespaceShort.CALDAV}:calendar-data`]: {},
        },
        depth: '1',
      })
      .then((response: DAVResponse[]) => {
        console.log('search event by keyword success %o', response)

        const eventDicts: { [key: string]: EventDict } = {}
        response.forEach((r) => {
          const originalUrl = new URL(client.account.homeUrl).origin
          const url = originalUrl + r.href
          const hasInstance = classInfo.instances.some((i: EventInstance) => i.serverId === url)
          if (hasInstance) {
            return
          }
          const folder = classInfo.calendars.find((c: Calendar) => url.startsWith(c.url))

          const data = r.props!.calendarData!._cdata
          const vCalendar = parseICS(data)
          let masterEvent: VEvent | null = null
          Object.entries(vCalendar).forEach(([_, v]) => {
            if ((v as any).type === 'VEVENT') {
              masterEvent = v as VEvent
            }
          })
          const eventDict: any = {
            url: url,
            etag: 'SEARCH',
            color: (masterEvent as any).color || (folder as any)!.calendarColor._cdata,
            event: masterEvent!,
            folderId: folder!.url,
            dirty: true,
          }
          eventDicts[url] = eventDict
        })
        const instances = _createInstances(eventDicts as EventsDict, false, [], false)
        completeCallback(instances)
      })
      .catch((error: Error) => {
        console.error('search event by keyword failed : %o', error)
        completeCallback([])
      })
  }

  let fireCount = 0
  const responseUrls = new Set<string>()

  const _fireSearchEachProp = (
    keyword: string,
    startDateStr: string,
    endDateStr: string,
    prop: string,
    attrName: string | null = null,
  ): void => {
    fireCount++
    const hasAttr = attrName != null
    const xml =
      '<?xml version="1.0" encoding="utf-8" ?>' +
      '<C:calendar-query xmlns:C="urn:ietf:params:xml:ns:caldav">' +
      // '<D:prop xmlns:D="DAV:">' +
      // '<C:calendar-data/>' +
      // '</D:prop>' +
      '<C:filter>' +
      '<C:comp-filter name="VCALENDAR">' +
      '<C:comp-filter name="VEVENT">' +
      (startDateStr != null || endDateStr != null
        ? '<C:time-range' +
          (startDateStr != null ? ' start="' + startDateStr + '"' : '') +
          (endDateStr != null ? ' end="' + endDateStr + '"' : '') +
          '/>'
        : '') +
      '<C:prop-filter name="' +
      prop +
      '">' +
      (hasAttr ? '<C:param-filter name="' + attrName + '">' : '') +
      '<C:text-match match-type="contains">' +
      keyword +
      '</C:text-match>' +
      (hasAttr ? '</C:param-filter>' : '') +
      '</C:prop-filter>' +
      '</C:comp-filter>' +
      '</C:comp-filter>' +
      '</C:filter>' +
      '</C:calendar-query>'

    const client = getClient() as any as DAVClient
    client
      .davRequest({
        url: client.account.homeUrl,
        init: {
          method: 'REPORT',
          headers: {
            Depth: 'infinity',
          },
          body: xml,
        },
        convertIncoming: false,
      })
      .then((response: DAVResponse[]) => {
        if (response.length === 1 && response[0].href == null) {
          console.log('search event by keyword %o %o empty result', prop, attrName || '')
        } else {
          console.log('search event by keyword %o %o success %o', prop, attrName || '', response)
          response.forEach((r) => {
            responseUrls.add(r.href!)
          })
        }
        if (--fireCount === 0) {
          _completeProcess(responseUrls)
        }
      })
      .catch((error: Error) => {
        console.log('search event by keyword failed : %o', error)
        if (--fireCount === 0) {
          _completeProcess(responseUrls)
        }
      })
  }

  const startDateStr = dateUtils.utcDateString(startDate)
  const endDateStr = dateUtils.utcDateString(endDate)

  _fireSearchEachProp(keyword, startDateStr, endDateStr, 'SUMMARY')
  _fireSearchEachProp(keyword, startDateStr, endDateStr, 'DESCRIPTION')
  _fireSearchEachProp(keyword, startDateStr, endDateStr, 'LOCATION')
  // _fireSearchEachProp(keyword, 'ATTENDEE')
  // _fireSearchEachProp(keyword, 'ATTENDEE', 'CN')
  // _fireSearchEachProp(keyword, 'ORGANIZER')
  // _fireSearchEachProp(keyword, 'ORGANIZER', 'CN')
}

/**
 * 이벤트를 다른 캘린더로 이동
 * @param targetCalendar 대상 캘린더
 * @param eventServerId 이벤트 서버 ID
 * @param completeCallback 완료 콜백
 */
export const moveEvent = (
  targetCalendar: Calendar,
  eventServerId: string,
  completeCallback: SuccessCallback = (_success: boolean) => {},
): void => {
  const client = getClient() as any as DAVClient

  const targetUrl = targetCalendar.url + '/' + eventServerId.split('/').pop()
  client
    .davRequest({
      url: eventServerId,
      convertIncoming: false,
      init: {
        method: 'MOVE',
        headers: {
          Destination: targetUrl,
        },
        body: null,
      },
    })
    .then((response: DAVResponse[]) => {
      console.log('move event success %o', response)
      enableCalendarSyncByEventServerId(eventServerId)
      reloadEvents()
      completeCallback(true)
    })
    .catch((error: Error) => {
      console.log('move event failed : %o', error)
      completeCallback(false)
    })
}

/**
 * 이벤트 서버 ID로 캘린더 찾기
 * @param serverId 이벤트 서버 ID
 * @returns 캘린더 객체
 */
export const findCalendarByEventServerId = (serverId: string): Calendar | undefined => {
  return classInfo.calendars.find((cal: Calendar) => serverId.startsWith(cal.url))
}

/**
 * Free/Busy 정보 조회
 * @param targetUserEmails 대상 사용자 이메일 목록
 * @param startDate 시작 날짜
 * @param endDate 종료 날짜
 * @param completeCallback 완료 콜백
 */
export const fetchFreeBusy = (
  targetUserEmails: string[],
  startDate: Date | null,
  endDate: Date | null,
  completeCallback: FreeBusyCallback = (_freeBusy: FreeBusyResponse) => {},
): void => {
  if (targetUserEmails.length === 0 || startDate == null || endDate == null) {
    completeCallback({})
    return
  }

  const client = getClient() as any as DAVClient

  let requestCount = targetUserEmails.length

  const startDateStr = moment(startDate).utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
  const endDateStr = moment(endDate).utc().format('YYYY-MM-DDTHH:mm:ss[Z]')

  const xml = `<?xml version='1.0' encoding='UTF-8' ?>
<CAL:free-busy-query xmlns:CAL="urn:ietf:params:xml:ns:caldav">
	<CAL:time-range start="${startDateStr}" end="${endDateStr}" />
</CAL:free-busy-query>`

  const parseFreeBusy = (freebusy: any): FreeBusyEntry[] => {
    // Handle undefined or null freebusy data
    if (!freebusy || !Array.isArray(freebusy)) {
      return []
    }

    const result: FreeBusyEntry[] = []
    freebusy.forEach((entry: any) => {
      if (entry.end instanceof Date) {
        result.push({
          start: entry.start,
          end: entry.end,
          isAllday: false,
        })
      } else if (typeof entry.end === 'string') {
        if (entry.end.endsWith('D')) {
          //All day free busy
          const momentStart = moment(entry.start).utc()
          const dateStart = new Date(momentStart.year(), momentStart.month(), momentStart.date())
          const numDays = parseInt(entry.end.replace(/\D/g, ''), 10)
          const dateEnd = dateUtils.dateAddingDate(dateStart, numDays)
          result.push({
            start: dateStart,
            end: dateEnd,
            isAllday: entry.isAllday,
          })
        } else {
          console.error('invalid free busy entry : %o', entry)
        }
      } else {
        console.error('invalid free busy entry : %o', entry)
      }
    })
    return result
  }

  const freeBusyResponses: FreeBusyResponse = {}

  for (const targetUserEmail of targetUserEmails) {
    client
      .davRequest({
        url: client.serverUrl + '/user/' + targetUserEmail + '/',
        init: {
          method: 'REPORT',
          body: xml,
          headers: {
            Depth: '1',
          },
        },
        convertIncoming: false,
      })
      .then((response: DAVResponse[]) => {
        if (response.length > 0 && response[0].ok && response[0].raw != null) {
          const data = response[0].raw
          const parsedData = parseICS(data as any)
          Object.entries(parsedData).forEach(([_, v]) => {
            if ((v as any).type === 'VFREEBUSY') {
              freeBusyResponses[targetUserEmail] = parseFreeBusy((v as any).freebusy)
            }
          })
        }
        requestCount--
        if (requestCount === 0) {
          completeCallback(freeBusyResponses)
        }
      })
      .catch((error: Error) => {
        console.log('fetch free busy failed : %o', error)
        freeBusyResponses[targetUserEmail] = null
        requestCount--
        if (requestCount === 0) {
          completeCallback(freeBusyResponses)
        }
      })
  }
}
